import { useState } from 'react';
import MuiDatePicker from '@mui/lab/DatePicker';
import { Box, TextFieldProps as MuiTextFieldProps } from '@mui/material';
import clsx from 'clsx';
import Icon, { ICONS } from 'components/Icon';
import TextField from 'components/TextField';

type DatePickerProps = {
  /**
   * Callback to handle date change events. This gets called on each date selection event,
   * INCLUDING if a user picks a date on mobile date picker but doesnt click apply
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange?: (date: any, keyboardInputValue?: string | undefined) => void;
  /**
   * Callback to handle date selection events. This gets called if a date is clicked on Desktop picker,
   * or if a user has clicked a date and clicks apply on mobile.
   */
  onAccept?: (date: Date) => void;
  /**
   * Custom error boolean to overwrite built in error handling
   */
  error?: boolean;
  /**
   * Optional controlled value
   */
  value?: string | null;
  /**
   * Props to be forwarded to the Textfield
   */
  TextFieldProps?: MuiTextFieldProps;
  /**
   * An optional placeholder value
   */
  label?: string;
  /**
   * An optional minimum data
   */
  minDate?: Date;
  /**
   * Primary color to override theme
   */
  primaryColor?: string;
  /**
   * Class name to be forwarded to the Dialog component for the mobile date picker.
   */
  dialogClassName?: string;
};

const InputAdornment = () => <Icon icon={ICONS.CALENDAR} />;

/**
 * GMS Themed Date Picker component. This component renders MUI's Desktop Date Picker and
 * Mobile Date Picker based on pointer accuracy.
 *
 * Read more at https://mui.com/components/date-picker/
 */
const DatePicker = ({
  onChange,
  onAccept,
  error,
  value,
  TextFieldProps = {},
  dialogClassName,
  label,
  minDate
}: DatePickerProps): JSX.Element => {
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  return (
    <Box>
      <MuiDatePicker
        value={value ?? selectedDate}
        inputFormat="MM/dd/yyyy"
        className="idonateDatePicker-root"
        cancelText="Cancel"
        onChange={(date, keyboardInput) => {
          onChange?.(date, keyboardInput);
        }}
        onAccept={(date) => {
          setSelectedDate(date.toUTCString());
          onAccept?.(date);
        }}
        okText="Apply"
        minDate={minDate}
        showToolbar={false}
        DialogProps={{
          classes: {
            root: clsx('DatePickerDialog', dialogClassName)
          }
        }}
        renderInput={({ inputProps, ...params }) => {
          const hasError = error ?? params.error;
          return (
            <TextField
              {...params}
              {...TextFieldProps}
              inputProps={{
                ...inputProps,
                placeholder: label
              }}
              error={hasError}
            />
          );
        }}
        components={{
          OpenPickerIcon: InputAdornment
        }}
      />
    </Box>
  );
};

export default DatePicker;
